<script setup lang="ts">
/**
 * 商城 (公开站) 同步提示 — AppShell 顶栏.
 * 平时不显示; 商品改动排队 / 构建中显示 "商城更新中"; 最近一次构建失败显示红色提示, 点开可重试.
 * 轮询: 有任务时 5 秒, 空闲时 30 秒; 页面在后台时暂停.
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useAuthStore } from '../stores/auth';
import { useToast } from '../composables/useToast';
import {
  getPublicSiteStatus,
  triggerPublicSiteRebuild,
  type PublicSiteStatusDto,
} from '../api/publicSite';

const auth = useAuthStore();
const toast = useToast();

const status = ref<PublicSiteStatusDto | null>(null);
const expanded = ref(false);
const triggering = ref(false);

const BUSY_INTERVAL_MS = 5_000;
const IDLE_INTERVAL_MS = 30_000;
let timer: number | null = null;

type State = 'hidden' | 'busy' | 'failed';
const state = computed<State>(() => {
  const s = status.value;
  if (!s) return 'hidden';
  if (s.schedulerState === 'building' || s.schedulerState === 'pending') return 'busy';
  if (s.recentBuilds?.[0]?.status === 'Failed') return 'failed';
  return 'hidden';
});
const latest = computed(() => status.value?.recentBuilds?.[0] ?? null);

async function refresh() {
  // 未登录时不请求, 避免 401 触发全局登出打断 OAuth 回调
  if (!auth.token) return;
  try {
    status.value = await getPublicSiteStatus(5);
  } catch {
    // 顶栏提示拉取失败不打扰员工, 下次再试
  }
}

function schedule() {
  if (timer != null) window.clearTimeout(timer);
  timer = null;
  if (document.visibilityState !== 'visible') return;
  const delay = state.value === 'busy' ? BUSY_INTERVAL_MS : IDLE_INTERVAL_MS;
  timer = window.setTimeout(async () => {
    await refresh();
    schedule();
  }, delay);
}

async function onVisibility() {
  if (document.visibilityState === 'visible') await refresh();
  schedule();
}

async function retry() {
  if (triggering.value) return;
  triggering.value = true;
  try {
    await triggerPublicSiteRebuild('工作台手动重试');
    toast.success('已重新开始更新商城');
    expanded.value = false;
    await refresh();
    schedule();
  } catch (e) {
    toast.error(e);
  } finally {
    triggering.value = false;
  }
}

function timeText(iso?: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

onMounted(async () => {
  await refresh();
  schedule();
  document.addEventListener('visibilitychange', onVisibility);
});
onBeforeUnmount(() => {
  if (timer != null) window.clearTimeout(timer);
  document.removeEventListener('visibilitychange', onVisibility);
});
</script>

<template>
  <div v-if="state !== 'hidden'" :class="['site-chip', state]">
    <button
      type="button"
      class="chip-btn"
      :aria-expanded="state === 'failed' ? expanded : undefined"
      :disabled="state === 'busy'"
      :title="state === 'busy' ? '商品改动正在同步到商城, 通常 1 分钟内完成' : '点击查看并重试'"
      @click="expanded = !expanded"
    >
      <span class="dot" aria-hidden="true" />
      <span class="lbl">{{ state === 'busy' ? '商城更新中' : '商城更新失败' }}</span>
    </button>

    <div v-if="expanded && state === 'failed'" class="panel" role="dialog" aria-label="商城更新失败">
      <p class="msg">
        最近一次把商品同步到商城时失败了{{ latest ? ` (${timeText(latest.finishedAt ?? latest.startedAt)})` : '' }}.
        客户看到的可能还是旧内容. 可以先重试一次, 仍失败请联系技术.
      </p>
      <div class="ops">
        <button type="button" class="btn btn-outline btn-sm" @click="expanded = false">关闭</button>
        <button type="button" class="btn btn-primary btn-sm" :disabled="triggering" :aria-busy="triggering" @click="retry">
          {{ triggering ? '正在重试…' : '重试' }}
        </button>
      </div>
      <details v-if="latest?.errorMessage" class="detail">
        <summary>技术细节</summary>
        <pre>{{ latest.errorMessage }}</pre>
      </details>
    </div>
  </div>
</template>

<style scoped>
.site-chip { position: relative; }
.chip-btn {
  display: inline-flex; align-items: center; gap: 6px;
  height: var(--ctl-h-xs); padding: 0 10px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--border-default);
  background: var(--color-surface);
  font: inherit; font-size: var(--fs-caption); color: var(--text-secondary);
  cursor: pointer; white-space: nowrap;
}
.chip-btn:disabled { cursor: default; }
.dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.busy .dot { background: var(--warning); animation: chipPulse 1.2s ease-in-out infinite; }
.failed .chip-btn { color: var(--danger-text); border-color: color-mix(in oklab, var(--danger) 35%, var(--border-default)); background: var(--danger-bg); }
.failed .dot { background: var(--danger); }
@keyframes chipPulse { 50% { opacity: 0.35; } }

.panel {
  position: fixed;
  top: 58px; right: 16px;
  width: min(360px, calc(100vw - 24px));
  padding: var(--sp-3) var(--sp-4);
  background: var(--color-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-l);
  box-shadow: var(--shadow-pop);
  z-index: 100;
  display: flex; flex-direction: column; gap: var(--sp-3);
}
.msg { margin: 0; font-size: var(--fs-small); line-height: 1.6; color: var(--text-secondary); }
.ops { display: flex; justify-content: flex-end; gap: var(--sp-2); }
.detail summary { cursor: pointer; font-size: var(--fs-caption); border-radius: var(--radius-xs); color: var(--text-muted); }
.detail pre {
  margin: var(--sp-2) 0 0; max-height: 160px; overflow: auto;
  font-size: var(--fs-micro); line-height: 1.5; white-space: pre-wrap; word-break: break-all;
  background: var(--color-surface-secondary); padding: var(--sp-2); border-radius: var(--radius-s);
}

@media (max-width: 720px) {
  .chip-btn { padding: 0 var(--sp-2); }
  .panel { top: 52px; right: 12px; }
}
</style>
